import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Clock,
  CloudUpload,
  Database,
  Download,
  ExternalLink,
  History,
  Pencil,
  Play,
  Table2,
  Trash2,
} from 'lucide-react';
import { api, unduhBerkas } from '@/lib/api';
import { EditJobDialog } from '@/components/jobs/EditJobDialog';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { StatusBadge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/Modal';
import { EmptyState, ErrorState, PageLoader, TableSkeleton } from '@/components/ui/States';
import { Pagination, Table, TableWrapper, Td, Th, Tr } from '@/components/ui/Table';
import { useToast } from '@/components/ui/Toast';
import { SaveToCloudDialog } from '@/components/storage/SaveToCloudDialog';
import { useCan } from '@/store/auth';
import { useDebounced } from '@/hooks/useDebounced';
import {
  formatDateTime,
  formatDomain,
  formatDuration,
  formatNumber,
  formatRelative,
  truncate,
} from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * Halaman detail job - layar yang sama sekali tidak ada di rancangan awal,
 * padahal inilah tempat pengguna menghabiskan waktu paling banyak: melihat
 * data yang berhasil diambil dan memeriksa run yang gagal.
 */
export const JobDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const canEdit = useCan('member');

  const [tab, setTab] = useState('data');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['job', id],
    queryFn: () => api.get(`/jobs/${id}`),
    // Job yang sedang berjalan disegarkan berkala sebagai cadangan bila
    // koneksi realtime terputus.
    refetchInterval: (query) =>
      ['queued', 'running'].includes(query.state.data?.data?.status) ? 4_000 : false,
  });

  const runMutation = useMutation({
    mutationFn: () => api.post(`/jobs/${id}/run`),
    onSuccess: (response) => {
      toast.success('Crawl dimulai', { description: 'Progres akan tampil secara langsung.' });
      queryClient.invalidateQueries({ queryKey: ['job', id] });
      navigate(`/jobs/${id}/runs/${response.data.id}`);
    },
    onError: (err) => toast.error('Gagal memulai crawl', { description: err.message }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/jobs/${id}`),
    onSuccess: () => {
      toast.success('Job dihapus');
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      navigate('/jobs');
    },
    onError: (err) => toast.error('Gagal menghapus job', { description: err.message }),
  });

  if (isLoading) return <PageLoader label="Memuat job…" />;
  if (isError) {
    return (
      <Card>
        <ErrorState message={error?.message} requestId={error?.requestId} onRetry={refetch} />
      </Card>
    );
  }

  const job = data.data;
  const isActive = ['queued', 'running'].includes(job.status);

  return (
    <div className="space-y-4">
      <Link
        to="/jobs"
        className="inline-flex items-center gap-1.5 text-sm text-[var(--text-muted)] transition-colors hover:text-[var(--text-secondary)]"
      >
        <ArrowLeft className="size-3.5" />
        Kembali ke daftar job
      </Link>

      {/* Kepala halaman */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <h2 className="text-2xl font-extrabold tracking-tight text-[var(--text-primary)]">
              {job.name}
            </h2>
            <StatusBadge status={job.status} />
          </div>

          <a
            href={job.targetUrl}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="mt-1.5 inline-flex items-center gap-1.5 text-sm text-[var(--text-muted)] transition-colors hover:text-accent-600"
          >
            {formatDomain(job.targetUrl)}
            <ExternalLink className="size-3.5" />
          </a>

          {job.description && (
            <p className="mt-2 max-w-2xl text-sm text-[var(--text-secondary)]">{job.description}</p>
          )}
        </div>

        {canEdit && (
          <div className="flex shrink-0 gap-2">
            <Button
              variant="secondary"
              size="sm"
              icon={Pencil}
              onClick={() => setEditing(true)}
            >
              Sunting Konfigurasi
            </Button>
            <Button
              variant="dangerOutline"
              size="sm"
              icon={Trash2}
              onClick={() => setConfirmDelete(true)}
            >
              Hapus
            </Button>
            <Button
              icon={Play}
              onClick={() => runMutation.mutate()}
              loading={runMutation.isPending}
              disabled={isActive}
            >
              {isActive ? 'Sedang berjalan' : 'Jalankan Sekarang'}
            </Button>
          </div>
        )}
      </div>

      {/* Statistik ringkas */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MiniStat label="Total baris" value={formatNumber(job.stats.totalRecords)} icon={Database} />
        <MiniStat
          label="Tingkat keberhasilan"
          value={job.successRate != null ? `${job.successRate}%` : ' - '}
          icon={History}
        />
        <MiniStat
          label="Rata-rata durasi"
          value={formatDuration(job.stats.lastRunDurationMs)}
          icon={Clock}
        />
        <MiniStat
          label="Terakhir dijalankan"
          value={formatRelative(job.stats.lastRunAt)}
          icon={Play}
        />
      </div>

      {job.lastError?.message && (
        <div
          role="alert"
          className="rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 dark:border-danger-500/25 dark:bg-danger-500/10"
        >
          <p className="text-sm font-bold text-danger-700 dark:text-danger-500">
            Kegagalan terakhir
          </p>
          <p className="mt-0.5 text-sm text-danger-700/90 dark:text-danger-500/90">
            {job.lastError.message}
          </p>
        </div>
      )}

      {/* Tab: Data / Riwayat / Konfigurasi */}
      <Card className="overflow-hidden">
        <div
          role="tablist"
          aria-label="Bagian detail job"
          className="flex border-b border-[var(--border-subtle)]"
        >
          {[
            ['data', 'Data Hasil', Table2],
            ['runs', 'Riwayat Run', History],
            ['config', 'Konfigurasi', Database],
          ].map(([value, label, Icon]) => (
            <button
              key={value}
              role="tab"
              type="button"
              aria-selected={tab === value}
              onClick={() => setTab(value)}
              className={cn(
                'flex items-center gap-2 border-b-2 px-5 py-3.5 text-sm font-semibold transition-colors',
                tab === value
                  ? 'border-brand-950 text-[var(--text-primary)] dark:border-accent-500'
                  : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)]',
              )}
            >
              <Icon className="size-4" aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>

        {tab === 'data' && (
          <DataTab jobId={id} job={job} onSaveToCloud={() => setSaveOpen(true)} />
        )}
        {tab === 'runs' && <RunsTab jobId={id} />}
        {tab === 'config' && <ConfigTab job={job} />}
      </Card>

      <SaveToCloudDialog open={saveOpen} onClose={() => setSaveOpen(false)} job={job} />

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => deleteMutation.mutate()}
        loading={deleteMutation.isPending}
        destructive
        title="Hapus job ini?"
        description={`"${job.name}" akan dihapus beserta jadwalnya. Data hasil crawl yang sudah tersimpan tidak ikut terhapus.`}
        confirmLabel="Ya, hapus job"
      />

      {editing && <EditJobDialog job={job} onClose={() => setEditing(false)} />}
    </div>
  );
};

const MiniStat = ({ label, value, icon: Icon }) => (
  <Card className="flex items-center gap-3 p-4">
    <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-[var(--surface-sunken)] text-[var(--text-muted)]">
      <Icon className="size-[18px]" aria-hidden="true" />
    </div>
    <div className="min-w-0">
      <p className="truncate text-lg font-extrabold tabular text-[var(--text-primary)]">{value}</p>
      <p className="truncate text-xs text-[var(--text-muted)]">{label}</p>
    </div>
  </Card>
);

/** Tabel data hasil - kolomnya dibangun dari konfigurasi field job. */
const DataTab = ({ jobId, job, onSaveToCloud }) => {
  // Dipakai saat unduhan gagal. Sebelumnya tidak pernah diambil di sini, jadi
  // kegagalan ekspor berujung pada galat "toast is not defined" dan tab ini
  // ikut berhenti menampilkan apa pun.
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 350);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['job-records', jobId, page, debouncedSearch],
    queryFn: () =>
      api.get(`/jobs/${jobId}/records`, { params: { page, limit: 25, search: debouncedSearch } }),
    placeholderData: (previous) => previous,
  });

  const columns = job.fields.map((f) => f.name);
  const records = data?.data ?? [];
  const meta = data?.meta;

  if (isError) {
    return <ErrorState message={error?.message} onRetry={refetch} compact />;
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--border-subtle)] p-4">
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Cari di dalam data…"
          aria-label="Cari data"
          size="sm"
          className="max-w-xs"
        />
        <div className="ml-auto flex gap-2">
          {/* Unduh langsung untuk sekali pakai; simpan ke cloud untuk data yang
              ingin disimpan, dibagikan, atau dipublikasikan nanti. */}
          <Button
            variant="secondary"
            size="sm"
            icon={Download}
            disabled={!meta?.total}
            onClick={() =>
              unduhBerkas(`/jobs/${jobId}/export?format=csv`, `${job.name}.csv`).catch((err) =>
                toast.error('Ekspor gagal', { description: err.message }),
              )
            }
          >
            Unduh CSV
          </Button>
          <Button
            size="sm"
            icon={CloudUpload}
            disabled={!meta?.total}
            onClick={onSaveToCloud}
          >
            Simpan ke Cloud
          </Button>
        </div>
      </div>

      {isLoading ? (
        <TableSkeleton rows={6} columns={Math.min(columns.length, 5)} />
      ) : records.length === 0 ? (
        <EmptyState
          compact
          icon={Table2}
          title={debouncedSearch ? 'Tidak ada data yang cocok' : 'Belum ada data'}
          description={
            debouncedSearch
              ? 'Coba kata kunci yang lain.'
              : 'Jalankan job ini untuk mulai mengumpulkan data.'
          }
        />
      ) : (
        <>
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th className="w-12">#</Th>
                  {columns.map((column) => (
                    <Th key={column}>{column}</Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((record, index) => (
                  <Tr key={record.id}>
                    <Td className="tabular text-[var(--text-muted)]">
                      {(meta.page - 1) * meta.limit + index + 1}
                    </Td>
                    {columns.map((column) => (
                      <Td key={column}>
                        <CellValue value={record.data?.[column]} />
                      </Td>
                    ))}
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>

          <div className="border-t border-[var(--border-subtle)]">
            <Pagination
              page={meta.page}
              totalPages={meta.totalPages}
              total={meta.total}
              limit={meta.limit}
              onPageChange={setPage}
            />
          </div>
        </>
      )}
    </>
  );
};

/** Menampilkan nilai sel sesuai bentuknya - tautan jadi bisa diklik, dst. */
const CellValue = ({ value }) => {
  if (value == null || value === '') {
    return <span className="text-[var(--text-muted)]"> - </span>;
  }

  if (Array.isArray(value)) {
    return (
      <span className="text-[var(--text-secondary)]">
        {truncate(value.filter(Boolean).join(', '), 60)}
      </span>
    );
  }

  if (typeof value === 'number') {
    return <span className="tabular font-medium">{formatNumber(value)}</span>;
  }

  if (typeof value === 'string' && /^https?:\/\//.test(value)) {
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="inline-flex items-center gap-1 text-accent-600 hover:underline dark:text-accent-400"
      >
        {truncate(formatDomain(value), 32)}
        <ExternalLink className="size-3" />
      </a>
    );
  }

  return <span title={String(value)}>{truncate(String(value), 60)}</span>;
};

const RunsTab = ({ jobId }) => {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['job-runs', jobId, page],
    queryFn: () => api.get(`/jobs/${jobId}/runs`, { params: { page, limit: 15 } }),
    placeholderData: (previous) => previous,
  });

  const runs = data?.data ?? [];
  const meta = data?.meta;

  if (isLoading) return <TableSkeleton rows={5} columns={5} />;

  if (runs.length === 0) {
    return (
      <EmptyState
        compact
        icon={History}
        title="Belum pernah dijalankan"
        description="Tiap kali job ini dijalankan, hasilnya tercatat di sini, termasuk yang gagal beserta sebabnya."
      />
    );
  }

  return (
    <>
      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <Th>Waktu</Th>
              <Th>Status</Th>
              <Th align="right">Halaman</Th>
              <Th align="right">Baris</Th>
              <Th align="right">Durasi</Th>
              <Th className="hidden sm:table-cell">Pemicu</Th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <Tr key={run.id}>
                <Td>
                  <Link
                    to={`/jobs/${jobId}/runs/${run.id}`}
                    className="font-medium text-accent-600 hover:underline dark:text-accent-400"
                  >
                    {formatDateTime(run.createdAt)}
                  </Link>
                </Td>
                <Td>
                  <StatusBadge status={run.status} size="sm" />
                </Td>
                <Td align="right" className="tabular">
                  {formatNumber(run.progress?.pagesVisited ?? 0)}
                </Td>
                <Td align="right" className="tabular">
                  {formatNumber(run.progress?.recordsExtracted ?? 0)}
                </Td>
                <Td align="right" className="tabular">
                  {formatDuration(run.durationMs)}
                </Td>
                <Td className="hidden sm:table-cell">
                  {run.trigger === 'schedule' ? 'Terjadwal' : 'Manual'}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>

      <div className="border-t border-[var(--border-subtle)]">
        <Pagination
          page={meta.page}
          totalPages={meta.totalPages}
          total={meta.total}
          limit={meta.limit}
          onPageChange={setPage}
        />
      </div>
    </>
  );
};

const ConfigTab = ({ job }) => (
  <CardBody className="space-y-6">
    <section>
      <h3 className="text-sm font-bold text-[var(--text-primary)]">Cakupan crawl</h3>
      <dl className="mt-3 grid gap-x-8 gap-y-2.5 sm:grid-cols-2">
        {[
          ['Mesin', { auto: 'Otomatis', static: 'HTML statis', dynamic: 'Browser' }[job.engine]],
          ['Kedalaman', job.crawl.depth === 0 ? 'Halaman ini saja' : `${job.crawl.depth} level`],
          ['Maks. halaman', formatNumber(job.crawl.maxPages)],
          ['Jeda antar permintaan', `${job.crawl.delayMs} ms`],
          ['Selector item', job.crawl.itemSelector ?? ' - '],
          ['Selector paginasi', job.crawl.paginationSelector ?? ' - '],
          ['Hormati robots.txt', job.crawl.respectRobotsTxt ? 'Ya' : 'Tidak'],
          ['Buang duplikat', job.deduplicate ? 'Ya' : 'Tidak'],
        ].map(([label, value]) => (
          <div key={label} className="flex justify-between gap-4 text-sm">
            <dt className="text-[var(--text-muted)]">{label}</dt>
            <dd className="text-right font-medium text-[var(--text-primary)]">{value}</dd>
          </div>
        ))}
      </dl>
    </section>

    <section>
      <h3 className="text-sm font-bold text-[var(--text-primary)]">
        Field ({job.fields.length})
      </h3>
      <div className="mt-3 overflow-hidden rounded-lg border border-[var(--border-subtle)]">
        <table className="w-full text-sm">
          <thead className="bg-[var(--surface-sunken)]">
            <tr>
              <th className="px-4 py-2.5 text-left text-[11px] font-bold tracking-wider text-[var(--text-muted)] uppercase">
                Nama
              </th>
              <th className="px-4 py-2.5 text-left text-[11px] font-bold tracking-wider text-[var(--text-muted)] uppercase">
                Selector
              </th>
              <th className="px-4 py-2.5 text-left text-[11px] font-bold tracking-wider text-[var(--text-muted)] uppercase">
                Tipe
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border-subtle)]">
            {job.fields.map((field) => (
              <tr key={field._id ?? field.name}>
                <td className="px-4 py-2.5 font-semibold text-[var(--text-primary)]">
                  {field.name}
                </td>
                <td className="px-4 py-2.5 font-mono text-[12px] text-[var(--text-secondary)]">
                  {field.selector}
                </td>
                <td className="px-4 py-2.5 text-[var(--text-muted)]">{field.type}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  </CardBody>
);
